import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import {
  ColorLens,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  HexColorPicker,
  HexColorInput,
} from 'react-colorful';
import DropBoxUploader from './DropBoxUploader';

const SectionBackgroundSettings = ({ sectionId, sections, setSections }) => {
  const [open, setOpen] = useState(false);
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [showHeaderPicker, setShowHeaderPicker] = useState(false);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [showHeaderFontPicker, setShowHeaderFontPicker] = useState(false);

  const installation = useSelector(state => state.settings_dealerships?.installation || {});
  const dealership = installation.subdomain || 'unknown';

  const section = sections[sectionId] || {};
  const bg = section.background || { color: '', image: '' };
  const headerColor = section.headerColor || '#f5f5f5';
  const fontColor = section.fontColor || '#000000';
  const headerFontColor = section.headerFontColor || '#000000';

  const update = (key, value) => {
    setSections(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        ...(key === 'color' || key === 'image'
          ? { background: { ...(prev[sectionId]?.background || {}), [key]: value } }
          : { [key]: value }
        ),
      },
    }));
  };

  const handleCopyToAll = () => {
    const currentBg = { ...bg };
    const currentHeaderColor = headerColor;
    const currentFontColor = fontColor;
    const currentHeaderFontColor = headerFontColor;

    setSections(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(secId => {
        if (secId !== sectionId) {
          updated[secId] = {
            ...updated[secId],
            background: { ...currentBg },
            headerColor: currentHeaderColor,
            fontColor: currentFontColor,
            headerFontColor: currentHeaderFontColor,
          };
        }
      });
      return updated;
    });

    setOpen(false);
  };

  return (
    <>
      {/* Button to open modal */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<ColorLens />}
        onClick={() => setOpen(true)}
        sx={{ mt: 2 }}
      >
        Section Background
      </Button>

      {/* Modal with all background settings */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Section Background Settings
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Background Color */}
          <Typography variant="subtitle1" gutterBottom>
            Background Color
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ColorLens />}
              onClick={() => setShowBgPicker(!showBgPicker)}
            >
              {bg.color === '' ? 'Set Color' : 'Change Color'}
            </Button>

            <Box
              sx={{
                width: 60,
                height: 40,
                bgcolor: bg.color || '#ffffff',
                border: '2px solid #ccc',
                borderRadius: 1,
                boxShadow: 2,
              }}
            />

            {bg.color !== '' && (
              <Button size="small" onClick={() => update('color', '')}>
                Clear
              </Button>
            )}
          </Stack>

          {showBgPicker && (
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Stack direction="column" spacing={2} alignItems="center">
                <HexColorPicker
                  color={bg.color || '#ffffff'}
                  onChange={(color) => update('color', color)}
                />

                <HexColorInput
                  color={bg.color || '#ffffff'}
                  onChange={(color) => update('color', color)}
                  prefixed
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '1rem',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                  }}
                />
              </Stack>
            </Paper>
          )}

          {/* Section Font Color (moved under Background Color) */}
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Section Font Color (currently not set up)
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ColorLens />}
              onClick={() => setShowFontPicker(!showFontPicker)}
            >
              {fontColor === '#000000' ? 'Set Color' : 'Change Color'}
            </Button>

            <Box
              sx={{
                width: 60,
                height: 40,
                bgcolor: fontColor,
                border: '2px solid #ccc',
                borderRadius: 1,
                boxShadow: 2,
              }}
            />

            {fontColor !== '#000000' && (
              <Button size="small" onClick={() => update('fontColor', '')}>
                Clear
              </Button>
            )}
          </Stack>

          {showFontPicker && (
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Stack direction="column" spacing={2} alignItems="center">
                <HexColorPicker
                  color={fontColor}
                  onChange={(color) => update('fontColor', color)}
                />

                <HexColorInput
                  color={fontColor}
                  onChange={(color) => update('fontColor', color)}
                  prefixed
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '1rem',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                  }}
                />
              </Stack>
            </Paper>
          )}

          {/* Section Header Color */}
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 4 }}>
            Section Header Background Color
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ColorLens />}
              onClick={() => setShowHeaderPicker(!showHeaderPicker)}
            >
              {headerColor === '#f5f5f5' ? 'Set Color' : 'Change Color'}
            </Button>

            <Box
              sx={{
                width: 60,
                height: 40,
                bgcolor: headerColor,
                border: '2px solid #ccc',
                borderRadius: 1,
                boxShadow: 2,
              }}
            />

            {headerColor !== '#f5f5f5' && (
              <Button size="small" onClick={() => update('headerColor', '')}>
                Clear
              </Button>
            )}
          </Stack>

          {showHeaderPicker && (
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Stack direction="column" spacing={2} alignItems="center">
                <HexColorPicker
                  color={headerColor}
                  onChange={(color) => update('headerColor', color)}
                />

                <HexColorInput
                  color={headerColor}
                  onChange={(color) => update('headerColor', color)}
                  prefixed
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '1rem',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                  }}
                />
              </Stack>
            </Paper>
          )}

          {/* Section Header Font Color */}
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 4 }}>
            Section Header Font Color
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ColorLens />}
              onClick={() => setShowHeaderFontPicker(!showHeaderFontPicker)}
            >
              {headerFontColor === '#000000' ? 'Set Color' : 'Change Color'}
            </Button>

            <Box
              sx={{
                width: 60,
                height: 40,
                bgcolor: headerFontColor,
                border: '2px solid #ccc',
                borderRadius: 1,
                boxShadow: 2,
              }}
            />

            {headerFontColor !== '#000000' && (
              <Button size="small" onClick={() => update('headerFontColor', '')}>
                Clear
              </Button>
            )}
          </Stack>

          {showHeaderFontPicker && (
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Stack direction="column" spacing={2} alignItems="center">
                <HexColorPicker
                  color={headerFontColor}
                  onChange={(color) => update('headerFontColor', color)}
                />

                <HexColorInput
                  color={headerFontColor}
                  onChange={(color) => update('headerFontColor', color)}
                  prefixed
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '1rem',
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                  }}
                />
              </Stack>
            </Paper>
          )}

          {/* Background Image */}
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Background Image
          </Typography>

          <DropBoxUploader
            installation={dealership}
            siteid={`${dealership}_${sectionId}_bg`}
            value={bg.image || ''}
            onChange={(url) => update('image', url)}
          />

          {bg.image && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <img
                src={bg.image}
                alt="Section background preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  borderRadius: 8,
                  objectFit: 'cover',
                  border: '1px solid #ddd',
                }}
              />
              <br />
              <Button
                size="small"
                color="error"
                sx={{ mt: 1 }}
                onClick={() => update('image', '')}
              >
                Remove Image
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCopyToAll}
          >
            Copy to All Sections
          </Button>
          <Button onClick={() => setOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SectionBackgroundSettings;